import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as braze from "@braze/web-sdk";
import {
  useQueryParamsAsJson,
  setAnalyticsUserId,
} from "@tomocredit/common-fe/utils";
import axios from "axios";

interface BrazeContextType {
  isInitialized: boolean;
  getInstance: () => typeof braze;
}

interface BrazeProviderProps {
  children: ReactNode;
  options?: Record<string, any>;
}

const BrazeContext = createContext<BrazeContextType>({
  isInitialized: false,
  getInstance: () => {
    throw new Error("Braze not initialized yet");
  },
});

export const BrazeProvider: React.FC<BrazeProviderProps> = ({
  children,
  options = {},
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationRef = useRef(false);
  const prefilledEmail = new URLSearchParams(window.location.search)?.get(
    "email"
  );
  const { capturedUrlParams } = useQueryParamsAsJson(["email"]);

  const handlePrefilledEmail = async (email: string, params: any) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/newUserFromLanding`,
        {
          userId: email,
          capturedUrlParams: params,
        }
      );

      const guid = response.data?.guid;
      if (guid) {
        braze.changeUser(guid);
        setAnalyticsUserId(guid);
        // // skipping setEmail as user email is prefilled -> should be in braze already
        // braze.getUser()?.setEmail(email);
      }
    } catch (error) {
      console.error("Error tracking session with prefilled email:", error);
    }
  };

  // Initialize Braze SDK
  useEffect(() => {
    if (initializationRef.current) {
      return undefined;
    }

    if (
      !process.env.REACT_APP_BRAZE_API_ENDPOINT ||
      !process.env.REACT_APP_BRAZE_API_KEY
    ) {
      console.error("Braze API key or endpoint not provided");
      return undefined;
    }

    // set ref to true to prevent duplicate initialization
    initializationRef.current = true;

    const brazeOptions = {
      baseUrl: process.env.REACT_APP_BRAZE_API_ENDPOINT,
      enableLogging: process.env.NODE_ENV !== "production",
      ...options,
    };

    try {
      braze.initialize(process.env.REACT_APP_BRAZE_API_KEY, brazeOptions);
      setIsInitialized(true);

      if (prefilledEmail) {
        handlePrefilledEmail(prefilledEmail, capturedUrlParams);
      }

      // set up visibility change listener for proper session management
      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          braze.requestImmediateDataFlush();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Clean up
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        braze.requestImmediateDataFlush();
      };
    } catch (error) {
      console.error("Failed to initialize Braze SDK:", error);
      return undefined;
    }
  }, [options]);

  const contextValue = useMemo(
    () => ({
      isInitialized,
      getInstance: () => braze,
    }),
    [isInitialized]
  );

  return (
    <BrazeContext.Provider value={contextValue}>
      {children}
    </BrazeContext.Provider>
  );
};

export const useBraze = () => {
  const context = useContext(BrazeContext);
  if (!context) {
    throw new Error("useBraze must be used within a BrazeProvider");
  }
  return context;
};

export default BrazeContext;
